import { TenantState } from './../core/states/tenant-state/tenant.state';

import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { mergeMap, take, tap } from 'rxjs/operators';
import { AuthenticationService } from './services/authentication.service';
import { TokenService } from './services/token.service';
import { ErrorHandlerService } from '../core/error-handler/error-handler.service';
import { Store } from '@ngrx/store';
import { getCurrentTenantApiUrl } from '../core/states/tenant-state/tenant.selectors';
import { AuthData } from '../core/declarations/auth-data.interface';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private readonly apiUrls = new Set<string>();

  constructor(
    private tokenService: TokenService,
    private authenticationService: AuthenticationService,
    private errorHandlerService: ErrorHandlerService,
    tenantStore: Store<TenantState>
  ) {
    tenantStore.select(getCurrentTenantApiUrl).subscribe(url => {
      this.apiUrls.add(url);
    });
  }

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return this.authenticationService.getAuthData().pipe(
      take(1),
      mergeMap(authData => {
        const isOwnApiRequest = this.isOwnApiRequest(req.url);
        const isAuthenticatedRequest = !!authData && isOwnApiRequest;

        if (isAuthenticatedRequest) {
          const headers = {
            'access-token': authData.accessToken,
            client: authData.client,
            expiry: authData.expiry,
            'token-type': authData.tokenType,
            uid: authData.uid
          };

          req = req.clone({
            setHeaders: headers
          });
        }

        return next.handle(req).pipe(
          tap({
            next: (res: HttpEvent<unknown>) => this.handleResponse(res),
            error: (err: HttpEvent<unknown>) =>
              this.handleErrorResponse(
                err,
                isOwnApiRequest,
                isAuthenticatedRequest ? authData : null
              )
          })
        );
      })
    );
  }

  private isOwnApiRequest(requestUrl: string): boolean {
    // Prefix match (not regex): the old `req.url.match(url)` treated the API
    // URL as a pattern, so its dots matched any char and a foreign host could
    // leak the token. Iterate the Set directly — this runs on every request.
    for (const url of this.apiUrls) {
      if (requestUrl.startsWith(url)) return true;
    }
    return false;
  }

  private handleResponse(
    res: HttpResponse<unknown> | HttpErrorResponse | HttpEvent<unknown>
  ): void {
    if (res instanceof HttpResponse || res instanceof HttpErrorResponse) {
      this.tokenService.getAuthHeadersFromResponse(res);
    }
  }

  private static tokenExpired(authData: AuthData): boolean {
    const expiry = Number.parseInt(authData.expiry, 10);
    return Number.isFinite(expiry) && expiry * 1000 <= Date.now();
  }

  private handleErrorResponse(
    err: HttpEvent<unknown> | HttpErrorResponse,
    wasOwnApiRequest: boolean,
    sentAuthData: AuthData | null
  ): void {
    if (!(err instanceof HttpErrorResponse)) {
      return;
    }
    this.tokenService.getAuthHeadersFromResponse(err);
    if (err.status !== 401 || !wasOwnApiRequest) {
      return;
    }
    this.errorHandlerService.acknowledgeSessionError(err);
    if (!sentAuthData) {
      return;
    }
    if (!TokenInterceptor.tokenExpired(sentAuthData)) {
      this.errorHandlerService.reportLostSession(err.url);
    }
    this.authenticationService.logout({ skipSignOut: true });
  }
}
